<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-4 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <ValidationObserver
      v-if="showNewLetterFields || viewMode"
      ref="generateLetterForm"
    >
      <add
        v-model="model"
        :alert-missing-document="alertMissingDocument"
        :is-readonly="isReadonly || viewMode"
      />
    </ValidationObserver>
    <div class="row">
      <span
        v-if="areSignersTypeMissing"
        class="col-12 error-message"
        v-html="missingSignersType"
      />
    </div>
    <template v-if="!viewMode">
      <custom-separator class="pt-3 my-4" text="Lista de Cartas" />
      <custom-result-not-found
        v-if="!pagination?.data"
        text="No hay información disponible."
      />
      <div class="row">
        <div class="col-12">
          <letter-row
            v-for="(item, index) in pagination?.data"
            :key="index"
            class="mb-3"
            :item="item"
            @delete="removeSignature(index)"
            @refresh="fetchLetters({ page: 1 })"
            @view="viewSignature(item)"
          />
        </div>
      </div>
      <pagination
        v-if="showPagination"
        :data="pagination"
        :show-text="true"
        @pagination-go-page="goToPage"
      />
    </template>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import CustomResultNotFound from 'custom-result-not-found';
import CustomSeparator from 'custom-separator';
import Add from './components/Add.vue';
import alertMixin from '@/mixins/alert.mixin.js';
import Pagination from 'pagination';
import LetterRow from './components/LetterRow.vue';
import letterTypes from '@/modules/proposalProcess/letterTypes.js';
import { serialize as objectToFormData } from 'object-to-formdata';
import { getRenewalTermTypes } from '@/modules/proposalProcess/api.js';

import {
  addLetter,
  getLetters,
  addAwardLetter,
} from '@/modules/proposalProcess/api.js';

const PARTICIPANT_TYPES = {
  DESTINATARIO_DETERMINACION: 1,
  FIRMANTE_DETERMINACION: 2,
  DESTINATARIO_ADJUDICACION: 3,
  FIRMANTE_ADJUDICACION: 4,
};

export default {
  name: 'GenerateLetter',
  components: {
    PageHeader,
    CustomResultNotFound,
    CustomSeparator,
    Add,
    Pagination,
    LetterRow,
  },
  mixins: [alertMixin],
  data() {
    return {
      headerConfig: {
        title: 'Generación de Carta',
        icon: 'fas fa-file-alt',
        routes: ['Proceso de Competencias', 'Generación de Carta'],
      },
      model: {
        letterInfo: {
          fundExpirationDate: null,
          federalFundDate: null,
          stateFundDate: null,
          additionalRenewalPeriod: null,
          renewalTerm: null,
        },
        documents: [],
        signatures: [],
        letterTypeId: null,
      },
      pagination: {
        data: [],
        pageSize: 10,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
      viewMode: false,
      areSignersTypeMissing: false,
      missingSignersType: null,
      isReadonly: false,
      alertMissingDocument: false,
      renewalTerms: [],
    };
  },
  computed: {
    showPagination() {
      return (
        this.pagination.data.length > 0 &&
        this.pagination.totalItemCount > this.pagination.pageSize
      );
    },
    routeId() {
      return this.$route.params?.id ? Number(this.$route.params.id) : 0;
    },
    showNewLetterFields() {
      return this.pagination.totalItemCount == 0;
    },
    showAwardLetterAction() {
      const determinationLetter = this.pagination?.data.some(
        (x) => x.letterType.id == letterTypes.DETERMINED && !!x.documentId
      );

      return determinationLetter && this.pagination.totalItemCount == 1;
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.viewMode ? this.resetModel() : this.$router.go(-1);
          },
          visible: true,
        },
        {
          text: 'Crear Carta Determinación',
          icon: 'fas fa-envelope',
          variant: 'success',
          visible: this.showNewLetterFields,
          click: () => this.add(),
        },
        {
          text: 'Generar Carta Adjudicación',
          icon: 'fas fa-envelope',
          variant: 'success',
          visible: this.showAwardLetterAction,
          click: () => this.addAwardLetter(),
        },
      ];
    },
  },
  async mounted() {
    await this.fetchLetters({ page: this.pagination.currentPage });
    await this.getRenewalTermTypes();
  },
  methods: {
    async add() {
      this.alertMissingDocument = false;
      const isValid = await this.$refs.generateLetterForm.validate();
      if (!isValid) return;

      const requiredTypes = [
        PARTICIPANT_TYPES.DESTINATARIO_DETERMINACION,
        PARTICIPANT_TYPES.FIRMANTE_DETERMINACION,
        PARTICIPANT_TYPES.FIRMANTE_ADJUDICACION,
      ];

      const participantTypesInSignatures = this.model.signatures.map(
        (sig) => sig.participantType.id
      );

      // Filtramos los tipos que faltan
      const missingTypes = requiredTypes.filter(
        (type) => !participantTypesInSignatures.includes(type)
      );

      if (missingTypes.length > 0) {
        // Construimos el mensaje con el formato solicitado
        this.areSignersTypeMissing = true;
        this.missingSignersType =
          `• Favor de incluir:<br><br>` +
          missingTypes
            .map((type) => {
              switch (type) {
                case PARTICIPANT_TYPES.DESTINATARIO_DETERMINACION:
                  return '- Destinatario Determinación';
                case PARTICIPANT_TYPES.FIRMANTE_DETERMINACION:
                  return '- Firmante Determinación';
                case PARTICIPANT_TYPES.FIRMANTE_ADJUDICACION:
                  return '- Firmante Adjudicación';
                default:
                  return '';
              }
            })
            .join('<br>');

        this.ShowToast(
          'Error',
          'Se deben incluir firmas de cada tipo requerido.',
          'error'
        );
        return;
      } else {
        this.areSignersTypeMissing = false;
        this.missingSignersType = null;
      }

      if (this.model.documents.length === 0) {
        this.ShowToast('Error', 'Al menos 1 documento es requerido.', 'error');
        this.alertMissingDocument = true;
        return;
      }

      let payload = {
        fundExpirationDate: this.model.letterInfo?.fundExpirationDate,
        federalFundDate: this.model.letterInfo?.federalFundDate,
        stateFundDate: this.model.letterInfo?.stateFundDate,
        additionalRenewalPeriod:
          Number(this.model.letterInfo?.additionalRenewalPeriod) || 0,
        renewalTermId: this.model.letterInfo?.renewalTerm?.id,
        announcementId: this.routeId,
        letterTypeId: letterTypes.DETERMINED,
        signers: this.model.signatures,
      };

      let formData = objectToFormData(payload, {
        indices: true,
        nullsAsUndefineds: true,
      });

      if (this.model.documents.length) {
        this.model.documents.forEach((document, index) => {
          formData.append(`documents[${index}].file`, document.file);
          formData.append(
            `documents[${index}].documentTypeId`,
            document.documentType.id
          );
        });
      }

      await addLetter(formData);
      this.ShowToast('Éxito', 'Carta guardada Exitosamente.', 'success');
      await this.fetchLetters({ page: this.pagination.currentPage });
      this.resetModel();
      this.$refs.generateLetterForm.reset();
    },
    async addAwardLetter() {
      await addAwardLetter(this.routeId)
        .then(() => {
          this.ShowToast('Éxito', 'Carta generada Exitosamente.', 'success');
          this.fetchLetters({ page: this.pagination.currentPage });
        })
        .catch((error) => {
          this.ShowToast('Alerta', `${error.response.data.message}`, 'error');
          return;
        });
    },
    async goToPage(page) {
      this.pagination.currentPage = page;
      await this.fetchLetters({ page });
    },
    async fetchLetters(model) {
      if (model.page == undefined) {
        this.pagination.currentPage = 1;
      }

      let startRowIndex = 1;
      if (this.pagination.currentPage > 1) {
        startRowIndex = this.pagination.currentPage;
      }

      const { data } = await getLetters(
        this.routeId,
        startRowIndex,
        this.pagination.pageSize
      );

      this.letters = data;

      this.pagination = {
        ...this.pagination,
        data: data.data,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    resetModel() {
      this.model = {
        letterInfo: {
          fundExpirationDate: null,
          federalFundDate: null,
          stateFundDate: null,
          additionalRenewalPeriod: null,
          renewalTerm: null,
        },
        signatures: [],
        documents: [],
      };
      this.viewMode = false;
    },
    viewSignature(item) {
      this.viewMode = true;
      this.model.letterTypeId = item.letterType.id;

      this.model.letterInfo.fundExpirationDate = item.fundExpirationDate;
      this.model.letterInfo.federalFundDate = item.federalFundDate;
      this.model.letterInfo.stateFundDate = item.stateFundDate;
      this.model.letterInfo.additionalRenewalPeriod =
        item.additionalRenewalPeriod;
      this.model.letterInfo.renewalTerm = item.renewalTerm;

      let validSigner =
        item.letterType.id === letterTypes.DETERMINED
          ? [
              PARTICIPANT_TYPES.DESTINATARIO_DETERMINACION,
              PARTICIPANT_TYPES.FIRMANTE_DETERMINACION,
            ]
          : [
              PARTICIPANT_TYPES.DESTINATARIO_ADJUDICACION,
              PARTICIPANT_TYPES.FIRMANTE_ADJUDICACION,
            ];

      item.signers
        .filter((signer) => validSigner.includes(signer.participantType.id))
        .forEach((element) => {
          this.model.signatures.push({
            name: element.name,
            lastName: element.lasName,
            secondLastName: element.secondLastName,
            chargeName: element.chargeName,
            participantType: element.participantType,
            area: element.area,
          });
        });

      item.documents.forEach((element) => {
        this.model.documents.push({
          documentId: element.documentId,
          documentType: {
            id: element.documentTypeId,
            name: element.documentTypeName,
          },
        });
      });
    },
    async getRenewalTermTypes() {
      const { data } = await getRenewalTermTypes();
      this.renewalTerms = data;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/brand_variables.scss';
.error-message {
  color: red;
  font-size: 14px;
  margin-top: 10px;
  margin-left: 12px;
}
</style>
